import { DrugStatus } from "@/models/Drug/Drug";
import { DrugProduct, DrugSource } from "@/models/Drug/DrugProduct";
import DrugStatusEnum = DrugStatus.DrugStatusEnum;

export interface CompoundPatEd {
    id: number;
    compoundID: number;
    description: string;
    contraindications: string;
    administration: string;
    missedDoseInstructions: string;
    monitoring: string;
    sideEffects: string;
    storage: string;
}
export class CompoundPatEd {
    constructor(obj?:CompoundPatEd){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundIngredients {
    id: number;
    compoundID: number;
    drugSource: number;
    drugID: number;
    quantity: number;
    unitOfMeasure: string;
    drugName: string;
    ndc: string;
}
export class CompoundIngredients {
    constructor(obj?:Array<CompoundIngredients>){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundWarningLabels {
    id: number;
    compoundID: number;
    order: number;
    warning: string;
}
export class CompoundWarningLabels {
    constructor(obj?:Array<CompoundWarningLabels>){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundSteps {
    id: number;
    compoundID: number;
    stepNumber: number;
    description: string;
    requireWeight: boolean;
    requirePicture: boolean;
    weightTolerance: number | null;
    expectedWeight: number | null;
    requirePharmacistSignOff: boolean;
}
export class CompoundSteps {
    constructor(obj?:CompoundSteps){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundBatchSteps {
    id: number;
    compoundBatchID: number;
    stepNumber: number;
    description: string;
    expectedWeight: number | null;
    actualWeight: number | null;
    withinTolerance: boolean | null;
    photoId: number | null;
    completed: Date | null;
    completedBy: string | null;
    requirePicture: boolean;
    requireWeight: boolean;
    requirePharmacistSignOff: boolean;
    pharmacistVerified: boolean | null;
    pharmacistVerifiedBy: string | null;
    pharmacistVerifiedDate: Date | null;
}
export class CompoundBatchSteps {
    constructor(obj?:CompoundBatchSteps){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundBatchIngredients {
    id: number;
    compoundBatchID: number;
    drugID: number;
    drugSource: number;
    quantity: number;
    unitOfMeasure: string;
    lotNumber: string | null;
    expirationDate: Date | null;
    created: Date;
    createdBy: string;
    drugName: string | null;
}
export class CompoundBatchIngredients {
    constructor(obj?:CompoundBatchIngredients){
        if(obj) Object.assign(this, obj);
    }
}

export enum CompoundBatchStatus {
    InProgress = 0,
    Completed = 1,
    Cancelled = 2,
    AwaitingVerification = 3,
    Rejected = 4
}

export enum CompoundDosageForm {
    NotSpecified = 0,
    Capsule = 1,
    Ointment = 2,
    Cream = 3,
    Suppository = 4,
    Powder = 5,
    Emulsion = 6,
    Liquid = 7,
    Tablet = 10,
    Solution = 11,
    Suspension = 12,
    Lotion = 13,
    Shampoo = 14,
    Elixir = 15,
    Syrup = 16,
    Lozenge = 17,
    Enema = 18
}

const compoundDosageFormNames: Record<number, string> = {
    [CompoundDosageForm.Capsule]: 'Capsule',
    [CompoundDosageForm.Ointment]: 'Ointment',
    [CompoundDosageForm.Cream]: 'Cream',
    [CompoundDosageForm.Suppository]: 'Suppository',
    [CompoundDosageForm.Powder]: 'Powder',
    [CompoundDosageForm.Emulsion]: 'Emulsion',
    [CompoundDosageForm.Liquid]: 'Liquid',
    [CompoundDosageForm.Tablet]: 'Tablet',
    [CompoundDosageForm.Solution]: 'Solution',
    [CompoundDosageForm.Suspension]: 'Suspension',
    [CompoundDosageForm.Lotion]: 'Lotion',
    [CompoundDosageForm.Shampoo]: 'Shampoo',
    [CompoundDosageForm.Elixir]: 'Elixir',
    [CompoundDosageForm.Syrup]: 'Syrup',
    [CompoundDosageForm.Lozenge]: 'Lozenge',
    [CompoundDosageForm.Enema]: 'Enema',
};

export enum CompoundType {
    NotSpecified = 0,
    AntiInfective = 1,
    Ionotropic = 2,
    Chemotherapy = 3,
    PainManagement = 4,
    TpnPpn = 5,
    Hydration = 6,
    Ophthalmic = 7,
    Z0790 = 8,
    Z0791 = 9,
    Z0792 = 10,
    Z0793 = 11,
    Other = 99
}

export enum CompoundLevelOfEffort {
    NotSpecified = 0,
    Level1_Lowest = 11,
    Level2_LowComplexity = 12,
    Level3_ModerateComplexity = 13,
    Level4_HighComplexity = 14,
    Level5_Highest = 15
}

export const CompoundLevelOfEffortDisplayNames = [
    { enum: CompoundLevelOfEffort.NotSpecified, name: 'Not Specified' },
    { enum: CompoundLevelOfEffort.Level1_Lowest, name: 'Level 1 Lowest' },
    { enum: CompoundLevelOfEffort.Level2_LowComplexity, name: 'Level 2 Low Complexity' },
    { enum: CompoundLevelOfEffort.Level3_ModerateComplexity, name: 'Level 3 Moderate Complexity' },
    { enum: CompoundLevelOfEffort.Level4_HighComplexity, name: 'Level 4 High Complexity' },
    { enum: CompoundLevelOfEffort.Level5_Highest, name: 'Level 5 Highest' },
];

export interface CompoundBatch {
    id: number;
    batchNumber: string;
    compoundID: number;
    batchSize: number;
    packageSize: number | null;
    lotNumber: string | null;
    beyondUseDate: Date | null;
    storeId: number | null;
    rxNumber: number | null;
    rfNumber: number | null;
    status: CompoundBatchStatus;
    verifiedBy: string | null;
    verifiedDate: Date | null;
    compound: CompoundDrug | null;
    steps: CompoundBatchSteps[] | null;
    ingredients: CompoundBatchIngredients[] | null;
    created: Date;
    createdBy: string;
    storeName: string | null;
    stepCount: number | null;
    ingredientCount: number | null;
    requiredVerificationCount: number | null;
    completedVerificationCount: number | null;
}
export class CompoundBatch {
    constructor(obj?:CompoundBatch){
        if(obj) Object.assign(this, obj);
    }
}

export interface CompoundDrug extends DrugProduct {
    compoundID?: number;
    ndc: string;
    name: string;
    labelName: string | null;
    awp: number;
    awpManualOverride: boolean;
    packageSize: number;
    unitOfMeasure: string;
    formulaSize: number | null;
    minimumDispenseQuantity: number;
    enforceMinimumDispenseMultiples: boolean;
    deaClass: string;
    auditChance: number;
    instructions: string;
    dosageForm: number | null;
    routeOfAdministration: string | null;
    compoundType: number | null;
    levelOfEffort: number | null;
    status: DrugStatusEnum;
    patEd: CompoundPatEd;
    ingredients: Array<CompoundIngredients>;
    warningLabels: Array<CompoundWarningLabels> | null;
    steps: Array<CompoundSteps> | null;
}

export class CompoundDrug extends DrugProduct {
    constructor(compoundId?:number|null, obj?:CompoundDrug|DrugProduct){
        super(obj);
        this.compoundID = compoundId || 0;
        this.drugId = Number(this.compoundID);
        // this.status = new DrugStatus()
        this.activeStatus = DrugStatusEnum.Inactive;
        this.source = DrugSource.CompoundProduct;

        if(obj){
            Object.assign(this, obj)
            this.compoundID = obj.drugId;
        }
    }
    toString(long: boolean = false): string {
        const productName = this.name;
        let preferred = '';
        if (this.status == DrugStatusEnum.ActiveAndPreferred) {
            preferred = '\u2B50 ';
        }
        const doseFormName = this.dosageForm ? compoundDosageFormNames[this.dosageForm] : null;
        const doseFormDisplay = doseFormName ? ` (${doseFormName})` : '';

        // For hover (long version), show label name when it exists
        if (long && this.labelName) {
            return this.ndc ? `${preferred}(NG) CompoundID:${this.compoundID}, NDC:${this.ndc} - ${productName}${doseFormDisplay} ${this.packageSize ? ("- PS: " + this.packageSize) : ""}${this.quantityOnHand ? ' - QTY:'+this.quantityOnHand : ''} - Label: ${this.labelName}`
                : `${productName}${doseFormDisplay} - Label: ${this.labelName}`;
        }

        if (productName)
            return this.ndc ? `${preferred}(NG) CompoundID:${this.compoundID}, NDC:${this.ndc} - ${productName}${doseFormDisplay} ${this.packageSize ? ("- PS: " + this.packageSize) : ""}${this.quantityOnHand ? ' - QTY:'+this.quantityOnHand : ''}`
                : productName;
        else return '';
    }

    set activeStatus(val: DrugStatusEnum) {
        this.status = val;
    }

    get activeStatus(): DrugStatusEnum {
        return this.status;
    }

    get productNameShort():string {
        return this.name;
    }
    set productNameShort(val: string) {
        this.name = val;
    }

    get isCII():boolean{
        return this.deaClass == "schedule II";
    }

    get NDCFormat542():string{
        return CompoundDrug.formatNDC542(this.ndc);
    }

    static formatNDC542(value:string | null) : string {
        if(!value) return '';
        return `${value.substr(0, 5)}-${value.substr(5, 4)}-${value.substr(9)}`;
    }
}

export interface CompoundPresetIngredient {
    id: number;
    compoundPresetID: number;
    compoundIngredientID: number;
    drugID: number;
    drugSource: number;
    quantity: number;
    unitOfMeasure: string;
    drugName: string | null;
}
export class CompoundPresetIngredient {
    constructor(obj?: CompoundPresetIngredient) {
        if (obj) Object.assign(this, obj);
    }
}

export interface CompoundPreset {
    id: number;
    compoundID: number;
    name: string;
    isDefault: boolean;
    ingredients: CompoundPresetIngredient[];
}
export class CompoundPreset {
    constructor(obj?: CompoundPreset) {
        if (obj) Object.assign(this, obj);
    }
}

export namespace CompoundDrug {
    export class EquivalentCompoundDrug{
        compoundID? : number;
        ndc? : string;
        productName? : string;
        packageSize?: number;
    }
}
